import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, SponsorDetail } from '../api';
import { sponsorEndpoints } from '../api';
import { MediaPicker } from '../media/MediaPicker';
import { usePermissions } from '../session';
import { NotPermitted } from '../App';
import { Badge, Button, ErrorNote, Panel } from '../ui';

/**
 * Sponsors (D-87).
 *
 * A sponsor is kept once and pointed at from everywhere: the event page, the
 * ticket design, the program. Their mark is held in the variants a designer
 * actually needs — full color for the web, reversed for a dark background,
 * one-color black for a thermal ticket that has no color at all — so nobody
 * has to make a white version of a logo at nine on a Friday.
 */

const VARIANTS = [
  { code: 'COLOR', name: 'Full color', hint: 'On light backgrounds' },
  { code: 'REVERSED', name: 'Reversed', hint: 'On dark backgrounds' },
  { code: 'BLACK', name: 'Black', hint: 'One-color print and thermal tickets' },
  { code: 'WHITE', name: 'White', hint: 'Dark one-color print' },
] as const;

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

export default function SponsorsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const sponsors = useQuery({ queryKey: ['sponsors', showInactive], queryFn: () => sponsorEndpoints.list(showInactive) });
  const detail = useQuery({ queryKey: ['sponsor', selected], queryFn: () => sponsorEndpoints.detail(selected!), enabled: Boolean(selected) });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['sponsors'] });
    if (selected) void queryClient.invalidateQueries({ queryKey: ['sponsor', selected] });
  };
  const fail = (cause: ApiError) => setError(cause.message);

  const create = useMutation({
    mutationFn: () => sponsorEndpoints.create({ slug: slugify(name), name }),
    onSuccess: (result) => {
      setError(null);
      setAdding(false);
      setName('');
      setSelected(result.sponsor.id);
      refresh();
    },
    onError: fail,
  });

  if (!can('sponsors.view')) return <NotPermitted permission="sponsors.view" />;
  const manage = can('sponsors.manage');

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold text-ink-900">Sponsors</h1>
        <label className="ml-auto flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Include inactive
        </label>
        {manage ? (
          <Button variant="primary" onClick={() => setAdding((open) => !open)}>
            {adding ? 'Cancel' : 'New sponsor'}
          </Button>
        ) : null}
      </div>

      {adding ? (
        <Panel title="New sponsor" description="The short name is what merge fields and ticket designs refer to; it is made from the name and can be changed later.">
          <div className="flex flex-wrap items-end gap-3 px-5 py-4">
            <label className="text-sm">
              <span className="text-xs font-semibold text-ink-400 uppercase">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 block w-72 rounded border border-line bg-surface px-2 py-1.5" />
            </label>
            <p className="text-sm text-ink-600">
              Short name: <code className="rounded bg-page px-1">{slugify(name) || '—'}</code>
            </p>
            <Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
              Create
            </Button>
          </div>
        </Panel>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
        <Panel title={`${sponsors.data?.sponsors.length ?? 0} sponsors`}>
          <ul className="divide-y divide-line">
            {(sponsors.data?.sponsors ?? []).map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => setSelected(row.id)}
                  aria-pressed={selected === row.id}
                  className={`flex w-full items-center gap-3 px-5 py-3 text-left ${selected === row.id ? 'bg-accent-soft' : 'hover:bg-page'}`}
                >
                  {row.logo_file_id ? (
                    <img src={`/api/public/media/${row.logo_file_id}`} alt="" className="h-8 w-16 rounded bg-page object-contain" />
                  ) : (
                    <span className="grid h-8 w-16 place-items-center rounded bg-page text-xs text-ink-400">no logo</span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-900">{row.display_name ?? row.name}</span>
                    <span className="block text-xs text-ink-400">
                      {row.logos} logo{row.logos === 1 ? '' : 's'} · {row.events} event{row.events === 1 ? '' : 's'}
                    </span>
                  </span>
                  {row.active ? null : <Badge tone="neutral">inactive</Badge>}
                </button>
              </li>
            ))}
            {sponsors.data?.sponsors.length === 0 ? <li className="px-5 py-4 text-sm text-ink-600">No sponsors yet.</li> : null}
          </ul>
        </Panel>

        {detail.data?.sponsor ? (
          <SponsorDetailPanel key={detail.data.sponsor.id} sponsor={detail.data.sponsor} manage={manage} onChanged={refresh} onError={fail} />
        ) : (
          <Panel title="Details">
            <p className="px-5 py-4 text-sm text-ink-600">Choose a sponsor.</p>
          </Panel>
        )}
      </div>
    </div>
  );
}

function SponsorDetailPanel({ sponsor, manage, onChanged, onError }: { sponsor: SponsorDetail; manage: boolean; onChanged: () => void; onError: (cause: ApiError) => void }) {
  const [picking, setPicking] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: sponsor.name,
    display_name: sponsor.display_name ?? '',
    website_url: sponsor.website_url ?? '',
    contact_name: sponsor.contact_name ?? '',
    contact_email: sponsor.contact_email ?? '',
    contact_phone: sponsor.contact_phone ?? '',
    brand_guidelines: sponsor.brand_guidelines ?? '',
  });

  const update = useMutation({ mutationFn: (input: Record<string, unknown>) => sponsorEndpoints.update(sponsor.id, input), onSuccess: onChanged, onError });
  const setLogo = useMutation({ mutationFn: (input: { file_id: string; variant_code: string; is_default?: boolean }) => sponsorEndpoints.setLogo(sponsor.id, input), onSuccess: onChanged, onError });
  const removeLogo = useMutation({ mutationFn: (variant: string) => sponsorEndpoints.removeLogo(sponsor.id, variant), onSuccess: onChanged, onError });

  const field = (key: keyof typeof form, label: string, type = 'text') => (
    <label className="block text-sm">
      <span className="text-xs font-semibold text-ink-400 uppercase">{label}</span>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        disabled={!manage}
        className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5"
      />
    </label>
  );

  return (
    <div className="space-y-4">
      <Panel
        title={sponsor.display_name ?? sponsor.name}
        description={`Short name: ${sponsor.slug}`}
        actions={
          manage ? (
            <Button onClick={() => update.mutate({ active: sponsor.active ? false : true })}>{sponsor.active ? 'Make inactive' : 'Make active'}</Button>
          ) : null
        }
      >
        <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
          {field('name', 'Legal name')}
          {field('display_name', 'Shown to customers')}
          {field('website_url', 'Website', 'url')}
          {field('contact_name', 'Contact')}
          {field('contact_email', 'Contact email', 'email')}
          {field('contact_phone', 'Contact phone')}
          <label className="block text-sm sm:col-span-2">
            <span className="text-xs font-semibold text-ink-400 uppercase">What their agreement says about the mark</span>
            <textarea
              value={form.brand_guidelines}
              onChange={(e) => setForm({ ...form, brand_guidelines: e.target.value })}
              disabled={!manage}
              rows={3}
              placeholder="Always on white. Never stretched. Not beside a competitor."
              className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5"
            />
          </label>
          {manage ? (
            <div className="sm:col-span-2">
              <Button
                variant="primary"
                disabled={update.isPending}
                onClick={() =>
                  update.mutate({
                    name: form.name,
                    display_name: form.display_name || null,
                    website_url: form.website_url || null,
                    contact_name: form.contact_name || null,
                    contact_email: form.contact_email || null,
                    contact_phone: form.contact_phone || null,
                    brand_guidelines: form.brand_guidelines || null,
                  })
                }
              >
                Save
              </Button>
            </div>
          ) : null}
        </div>
      </Panel>

      <Panel title="Logos" description="One mark per background. A ticket printer has no color; a dark page needs a reversed mark.">
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
          {VARIANTS.map((variant) => {
            const logo = sponsor.logos.find((l) => l.variant_code === variant.code) ?? null;
            return (
              <div key={variant.code} className="rounded border border-line p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-ink-900">{variant.name}</h3>
                  {logo?.is_default ? <Badge tone="ok">default</Badge> : null}
                </div>
                <p className="text-xs text-ink-400">{variant.hint}</p>
                <div className={`mt-2 grid h-24 place-items-center rounded ${variant.code === 'REVERSED' || variant.code === 'WHITE' ? 'bg-shell-900' : 'bg-page'}`}>
                  {logo ? <img src={logo.url} alt={`${sponsor.name} ${variant.name}`} className="max-h-20 max-w-full object-contain" /> : <span className="text-xs text-ink-400">none</span>}
                </div>
                {logo?.width_px ? <p className="mt-1 text-xs text-ink-400">{logo.width_px}×{logo.height_px} {logo.content_type.replace('image/', '')}</p> : null}
                {manage ? (
                  <div className="mt-2 flex gap-2">
                    <Button onClick={() => setPicking(variant.code)}>{logo ? 'Replace' : 'Choose'}</Button>
                    {logo ? (
                      <>
                        {logo.is_default ? null : (
                          <Button onClick={() => setLogo.mutate({ file_id: logo.file_id, variant_code: variant.code, is_default: true })}>Make default</Button>
                        )}
                        <Button variant="danger" onClick={() => removeLogo.mutate(variant.code)}>
                          Remove
                        </Button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Events">
        {sponsor.events.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-600">Not attached to an event yet. Attach a sponsor from the event&rsquo;s own page.</p>
        ) : (
          <ul className="divide-y divide-line">
            {sponsor.events.map((event) => (
              <li key={event.event_id} className="flex items-center gap-3 px-5 py-2 text-sm">
                <a href={`/events/${event.event_id}`} className="text-accent underline-offset-2 hover:underline">
                  {event.title}
                </a>
                {event.tier_code ? <Badge tone="accent">{event.tier_code.toLowerCase()}</Badge> : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <MediaPicker
        open={picking !== null}
        onClose={() => setPicking(null)}
        category="SPONSOR"
        title="Choose a logo"
        onChoose={(asset) => {
          if (picking) setLogo.mutate({ file_id: asset.id, variant_code: picking, is_default: sponsor.logos.length === 0 });
        }}
      />
    </div>
  );
}
